import { TFunction } from 'i18next';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { ZodSchema } from 'zod';

interface ValidationError {
  errorMessage: string;
  propertyName: string;
}

interface StepFormErrorNavigationOptions<TFormValues extends FieldValues> {
  // The schemas for each step
  stepSchemas: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: number]: ZodSchema | { shape: Record<string, any> } | string[]
  };
  // Current form methods from react-hook-form
  formMethods: Pick<UseFormReturn<TFormValues>, 'formState' | 'setError'>;
  // Function to navigate to a specific step
  goToStep: (step: number) => void;
  // Error data from API response (optional)
  apiErrors?: ValidationError[];
  // Translation function (optional, for error messages)
  t?: TFunction;
  // Show toast function (optional, for displaying errors not tied to fields)
  showToast?: (message: string, type: 'error' | 'success' | 'warning' | 'info') => void;
}

/**
 * Utility function to handle form error navigation between steps
 * Works with both client-side and server-side validation errors
 */
export function handleStepFormErrorNavigation<TFormValues extends FieldValues>(
  options: StepFormErrorNavigationOptions<TFormValues>
): void {
  const { stepSchemas, formMethods, goToStep, apiErrors, t, showToast } = options;
  const { formState, setError } = formMethods;

  // Prepare step fields mapping
  const stepFields: { [key: number]: string[] } = {};

  // Process each schema to extract field names
  Object.entries(stepSchemas).forEach(([stepIndex, schema]) => {
    const step = Number(stepIndex);

    if (Array.isArray(schema)) {
      // If schema is already a string array of field names
      stepFields[step] = schema;
    } else if ('shape' in schema) {
      // If schema is a Zod schema with shape property
      stepFields[step] = Object.keys(schema.shape);
    } else {
      // For other schema types, try to extract keys
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const shape = (schema as any)._def?.schema?.shape;
        if (shape) {
          stepFields[step] = Object.keys(shape);
        }
      } catch {
        console.warn(`Could not extract fields from schema for step ${step}`);
      }
    }
  });

  // If we have API errors, map them and set form errors
  if (apiErrors && apiErrors.length > 0) {
    // Find which step to navigate to
    const targetStep = Object.entries(stepFields).find(([_, fields]) =>
      apiErrors.some(({ propertyName }) => fields.includes(propertyName))
    );

    // Set errors from API response
    apiErrors.forEach(({ errorMessage, propertyName }) => {
      if (propertyName) {
        try {
          const message = t
            ? t(`errorCodes.${errorMessage}`, { fieldName: t(`common.${propertyName}`) })
            : errorMessage;

          // Use 'as any' to bypass TypeScript's strict checking since we can't guarantee
          // that propertyName is a valid key of TFormValues at compile time
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setError(propertyName as any, {
            message,
            type: 'manual'
          });
        } catch (e) {
          console.warn(`Could not set error for field: ${propertyName}`, e);
        }
      }
    });

    // Navigate to the step with errors
    if (targetStep) {
      goToStep(Number(targetStep[0]));
    } else if (showToast && apiErrors[0]) {
      // If no matching step found, show a toast with the error
      const errorMessage = apiErrors[0].errorMessage;
      const propertyName = apiErrors[0].propertyName;

      const message = t
        ? t(errorMessage ? `errorCodes.${errorMessage}` : 'common.error', {
          fieldName: propertyName ? t(`common.${propertyName}`) : undefined
        })
        : (errorMessage || 'An error occurred');

      showToast(message, 'error');
    }
  } else {
    // Handle client-side validation errors (from react-hook-form)
    const errorFieldNames = Object.keys(formState.errors);
    const targetStep = Object.entries(stepFields).find(([_, fields]) =>
      errorFieldNames.some((field) => fields.includes(field))
    );

    if (targetStep) {
      goToStep(Number(targetStep[0]));
    }
  }
}
